import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCog } from "lucide-react";

const ROLES = ["SUPER_ADMIN", "AGENCY_ADMIN", "RECEPTIONIST", "COUNTER_AGENT", "DISPLAY_ONLY", "AUDITOR"];

const EMPTY = { email: "", full_name: "", role: "RECEPTIONIST", password: "", agency_id: null, is_active: true };

export default function UsersPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const refresh = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { refresh(); api.get("/agencies").then((r) => setAgencies(r.data)); }, []);

  const submit = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Utilisateur mis à jour");
      } else {
        await api.post("/users", form);
        toast.success("Utilisateur créé");
      }
      setOpen(false); setEditing(null); setForm(EMPTY); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const onEdit = (u) => { setEditing(u); setForm({ ...EMPTY, ...u, password: "" }); setOpen(true); };
  const onDelete = async (u) => {
    if (!confirm(`Supprimer ${u.email} ?`)) return;
    await api.delete(`/users/${u.id}`); toast.success("Supprimé"); refresh();
  };
  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); refresh();
  };

  const agencyName = (id) => agencies.find((a) => a.id === id)?.name || "—";
  const isSuper = user?.role === "SUPER_ADMIN";

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-3xl">Utilisateurs</h1>
          <p className="text-slate-500 text-sm mt-1">Gestion des comptes et des rôles.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="add-user-button">
              <Plus className="w-4 h-4 mr-1" /> Nouvel utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouvel"} utilisateur</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Nom complet</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Rôle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.filter((r) => isSuper || r !== "SUPER_ADMIN").map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Agence</Label>
                  <Select value={form.agency_id || "none"} onValueChange={(v) => setForm({ ...form, agency_id: v === "none" ? null : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{editing ? "Nouveau mot de passe (optionnel)" : "Mot de passe"}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Actif</Label></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} className="bg-[#DC2626] hover:bg-[#B91C1C]">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="text-left p-3">Utilisateur</th><th className="text-left">Email</th><th>Rôle</th><th>Agence</th><th>Actif</th><th></th></tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-t border-slate-200">
                <td className="p-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <UserCog className="w-4 h-4 text-slate-500" />
                  </div>
                  <span className="font-medium">{u.full_name}</span>
                </td>
                <td className="text-slate-600">{u.email}</td>
                <td className="text-center"><span className="text-xs px-2 py-0.5 bg-slate-100 rounded font-mono">{u.role}</span></td>
                <td className="text-center text-slate-600 text-xs">{agencyName(u.agency_id)}</td>
                <td className="text-center"><Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} /></td>
                <td className="text-right p-3">
                  <Button size="icon" variant="ghost" onClick={() => onEdit(u)}><Pencil className="w-4 h-4" /></Button>
                  {isSuper && <Button size="icon" variant="ghost" onClick={() => onDelete(u)}><Trash2 className="w-4 h-4 text-[#DC2626]" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
